import Button, { ButtonWithTooltip } from "Components/button";
import { manageOverlay } from "Components/overlaysContext/overlaysManager";
import withOverlaysContext from "Components/overlaysContext/withOverlaysContext";
import RotatedArrow from "Components/rotatedArrow";
import { themeVariables } from "Components/theme";
import classNames from "classnames";
import { Popover } from "devextreme-react/popover";
import constant from "lodash/constant";
import includes from "lodash/includes";
import isEmpty from "lodash/isEmpty";
import React, { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { v4 as uuid } from "uuid";
import MenuList from "./menuList/menuList";
import buildPopoverId from "./utils/buildPopoverId";
import buildTogglerId from "./utils/buildTogglerId";
const SIZES = {
    MENU_ITEM_ICON: 16
};
const imgIconStyle = { width: `${SIZES.MENU_ITEM_ICON}px`, height: `${SIZES.MENU_ITEM_ICON}px` };
const handleStopPropagation = (evt) => {
    evt.stopPropagation();
};
const handlePreventDefault = (evt) => {
    evt.preventDefault();
};
const renderMenuItem = (item) => item.render ? (item.render()) : (React.createElement("div", { className: "grid-cols-auto with-gap-xs" },
    item.icon && (React.createElement("i", { className: classNames(item.icon, !includes(item.icon, "font-h") && "font-h3") })),
    item.iconSrc && React.createElement("img", { src: item.iconSrc, style: imgIconStyle }),
    item.textClassName ? React.createElement("span", { className: item.textClassName }, item.text) : item.text));
const defaultMenuPosition = {
    my: "right top",
    at: "right bottom",
    collision: "fit flip"
};
const defaultContextMenuPosition = {
    my: "left top",
    at: "left top",
    of: document.body,
    collision: "none"
};
function Menu(props) {
    const [open, setOpen] = useState(false);
    const [menuItems, setMenuItems] = useState(undefined);
    const [position, setPosition] = useState(defaultMenuPosition);
    const baseId = useMemo(() => props.id || `id${uuid()}`, [props.id]);
    const togglerId = buildTogglerId(baseId);
    const popoverId = buildPopoverId(baseId);
    const popoverRef = useRef(null);
    const wrapperAttr = useMemo(() => ({
        id: popoverId,
        height: "auto",
        class: classNames("popover-as-dropdown", props.popoverClassName),
        zIndex: props.zIndex
    }), [popoverId, props.zIndex, props.popoverClassName]);
    useEffect(() => () => {
        manageOverlay(popoverRef.current, props.overlaysManager, false);
    }, []);
    useLayoutEffect(() => {
        setMenuItems(props.items);
    }, [props.items]);
    useLayoutEffect(() => {
        if (props.mode === "menu") {
            const basePosition = props.position || defaultMenuPosition;
            setPosition(basePosition.offset || !props.positionOffset
                ? basePosition
                : { ...basePosition, offset: props.positionOffset });
        }
    }, [props.position, props.positionOffset, props.mode]);
    const changeOverlayShow = useCallback((newShow) => {
        setOpen(prev => {
            if (newShow === prev)
                return prev;
            manageOverlay(popoverRef.current, props.overlaysManager, newShow);
            return newShow;
        });
    }, [props.overlaysManager]);
    const handleDropdownToggleClick = useCallback(() => changeOverlayShow(!open), [open, changeOverlayShow]);
    const handleHideMenuOverlay = useCallback(() => {
        changeOverlayShow(false);
    }, [changeOverlayShow]);
    useEffect(() => {
        if (props.menuRef) {
            if (!props.menuRef.current)
                props.menuRef.current = {};
            props.menuRef.current.setMenuItems = (items) => {
                setMenuItems(items);
            };
            props.menuRef.current.hide = () => {
                changeOverlayShow(false);
            };
            props.menuRef.current.show = (x, y) => {
                setPosition({
                    ...defaultContextMenuPosition,
                    offset: { x, y }
                });
                changeOverlayShow(true);
            };
        }
    }, [changeOverlayShow]);
    const handlePopoverContentClicked = useCallback(() => {
        changeOverlayShow(false);
    }, []);
    if (isEmpty(menuItems))
        return null;
    return (React.createElement("div", { role: "none", className: props.className, onClick: handleStopPropagation, onContextMenu: handlePreventDefault },
        props.mode === "menu" &&
            React.createElement(props.togglerTitle ? ButtonWithTooltip : Button, {
                id: togglerId,
                className: classNames(open && "active", props.togglerClassName),
                onClick: handleDropdownToggleClick,
                width: props.togglerWidth,
                height: props.togglerHeight,
                disabled: props.disabled,
                popoverContent: props.togglerTitle
            }, props.togglerIconClassName && (React.createElement("i", { className: classNames(props.togglerIconClassName, !includes(props.togglerIconClassName, "font-h") && "font-h4") })), props.togglerText, props.togglerHasRotatedArrow && (React.createElement(RotatedArrow, { className: "flex-box-centered-content ml-0_5 text-light-on-surface-variant", open: open, size: 10 }))),
        React.createElement(Popover, { ref: popoverRef, wrapperAttr: wrapperAttr, target: props.mode === "menu" ? `#${togglerId}` : undefined, container: (props.getOverlaysContainer && props.getOverlaysContainer()) || document.body, position: position, visible: open, onHidden: handleHideMenuOverlay, hideOnParentScroll: props.hideOnParentScroll, hideOnOutsideClick: true, deferRendering: false, animation: undefined },
            React.createElement("div", { role: "none", className: "popover-content", onClick: handlePopoverContentClicked },
                React.createElement(MenuList, { menuRef: props.menuRef, items: menuItems, textSelector: renderMenuItem, keySelector: (item) => item.text || "", showSearchInputWhenItemsLengthIsGreaterThan: undefined, selectedKey: props.selectedItemText, subMenuZIndex: props.zIndex, subMenuPopoverClassName: props.popoverClassName, searchStylingMode: "filled" })))));
}
export default memo(withOverlaysContext(Menu));
Menu.defaultProps = {
    getOverlaysContainer: constant(undefined),
    togglerIconClassName: "icon-cog",
    togglerWidth: themeVariables["input-height-base"],
    togglerHeight: themeVariables["input-height-base"],
    position: defaultMenuPosition,
    positionOffset: "0",
    mode: "menu"
};
